import { ScalarField, ScalarFieldPoint, IterationBounds } from './scalar-field';
/*
 * implementation of the marching squares method (see https://en.wikipedia.org/wiki/Marching_squares)
 * Only one method is exported:
 * getIsolines = ( scalarField: ScalarField, thresholds: number[] ): { value:number, lines: LineString[][] }[]
 */

type Coord = [number, number];

/**
 * ScanPoint is one of the corners of a square in the Marching square algorithm. Since the algorithm 
 * travels from left to right and from top to bottom, the isoline that leaves the square at the right side
 * , if any,  does not need to be kept by this structure. The isoline that leaves the bottom of the square, 
 * however, needs to be recorded by this structure.  
 * 
 * @property scalar The scalar for this corner
 * @property type an array where each entry indicates wheteher it is less (0) then it's corresponding threshold (isoline) value or not (1). '
 * @property downPropagatedIsolines for each threshold an index that references the isoline that leaves the square via the bottom
 * @property rightSidePropagatedIsolines squares in the last column may have to form the clipped boundary of an isoline. This property keeps track if that is the case
 * @property bottomSidePropagatedIsolines squares in the last row may have to form the clipped boundary of an isoline. This property keeps track if that is the case
 */
type ScanPoint = {
	scalar: ScalarFieldPoint,
	type: (0 | 1)[]//type per threshold
	downPropagatedIsolines: number[];
	rightSidePropagatedIsolines: number[];
	bottomSidePropagatedIsolines: number[];
	
	squareType: number[];//for debugging
}

type IsolineSegment = {
	vertices: Coord[],
	helper: boolean //when false , it is part of the visible isoline; when true it is a helper section for isobands
}

type Isoline = IsolineSegment[] //A single isoline can consist of multiple segments

type ScalarFieldIsoline = { //A scalar field can contain multiple isolines for a given value
	value: number,
	isolines: Isoline[],
	isolineKeyMap: Map<number, number>
}

export interface LineString {
	points: number[][],
	boundary: boolean//when false , it is part of the visible isoline; when true it is a helper section for isobands
}

const loggingEnabled = false;

const log = (bottomRightScanPoint: ScanPoint, type: number, topIsolineIndex: number, topIsoline: Isoline, leftIsolineIndex: number, leftIsoline: Isoline) =>
	console.trace('Unexpected marching squares scanning state: scanPoint=%o, type=%s top(%s)=%o left(%s)=%o', bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);

const isolineToString = (isoline: Isoline, offset: Coord, divisor: number, precision: number): string => {
	let str = '[';
	if (isoline) {
		isoline.forEach(segment => segment.vertices.forEach(coord => str += ` (${((coord[0] - offset[0]) / divisor).toFixed(precision)}, ${((coord[1] - offset[1]) / divisor).toFixed(precision)})`))
	}
	str += ' ]';
	return str
}
const fieldlineToString = (fieldline: ScalarFieldIsoline, offset: Coord, divisor: number, precision: number): string => {
	let str = `'{\n  value: ${fieldline.value}}`;
	fieldline.isolines.forEach((isoline, index) => {
		str += `\n iso ${index}: ${isolineToString(isoline, offset, divisor, precision)}`;
	})
	str += `\n  keys: `
	fieldline.isolineKeyMap.forEach((value, key) => str += ' ' + key + '->' + value)
	return str + '\n}';
}
const toVertex = (scanPoint: ScanPoint): Coord => [scanPoint.scalar.position.x, scanPoint.scalar.position.y];

const bottomIntersection = (value: number, leftScanPoint: ScanPoint, rightScanPoint: ScanPoint): Coord => [
	leftScanPoint.scalar.position.x + ((rightScanPoint.scalar.position.x - leftScanPoint.scalar.position.x) * (value - leftScanPoint.scalar.value) / (rightScanPoint.scalar.value - leftScanPoint.scalar.value)),
	leftScanPoint.scalar.position.y]

const rightIntersection = (value: number, topScanPoint: ScanPoint, bottomtScanPoint: ScanPoint): Coord => [
	bottomtScanPoint.scalar.position.x,
	bottomtScanPoint.scalar.position.y + ((topScanPoint.scalar.position.y - bottomtScanPoint.scalar.position.y) * (value - bottomtScanPoint.scalar.value) / (topScanPoint.scalar.value - bottomtScanPoint.scalar.value))]

const prependSegment = (segment: IsolineSegment, isoline: Isoline): Isoline => {
	isoline.unshift(segment);
	return isoline;
}
const appendSegment = (segment: IsolineSegment, isoline: Isoline): Isoline => {
	isoline.push(segment);
	return isoline;
}
const prependVertex = (vertex: Coord, isoline: Isoline): Isoline => {
	isoline[0].vertices.unshift(vertex);
	return isoline;
}
const appendVertex = (vertex: Coord, isoline: Isoline): Isoline => {
	isoline[isoline.length - 1].vertices.push(vertex);
	return isoline;
}
const replaceFirstVertex = (vertex: Coord, isoline: Isoline): Isoline => {
	isoline[0].vertices.splice(0, 1, vertex);
	return isoline
}
const replaceLastVertex = (vertex: Coord, isoline: Isoline): Isoline => {
	isoline[isoline.length - 1].vertices.splice(isoline[isoline.length - 1].vertices.length - 1, 1, vertex);
	return isoline
}
const getFirstVertex = (isoline: Isoline): Coord => {
	return isoline[0].vertices[0];
}
const getLastVertex = (isoline: Isoline): Coord => {
	return isoline[isoline.length - 1].vertices[isoline[isoline.length - 1].vertices.length - 1];
}

const marchSquares = (iterator: Iterable<ScalarFieldPoint>, iterationBounds: IterationBounds, thresholds: number[]): { value: number, lines: LineString[][] }[] => {
	//the previous row we were scanning. This scanline forms the top corners of the squares of the current scanline
	let prevScanLine: ScanPoint[] = undefined;
	//the current row we are scanning
	let scanLine: ScanPoint[] = [];
	let topRightScanPoint: ScanPoint;
	let topLeftScanPoint: ScanPoint;
	let bottomRightScanPoint: ScanPoint;
	let bottomLeftScanPoint: ScanPoint;

	let rightPropagatedIsolines: number[] = thresholds.map(_t => undefined);
	let fieldLines: ScalarFieldIsoline[] = thresholds.map(threshold => {
		return {
			value: threshold,
			isolines: <Isoline[]>[],
			isolineKeyMap: new Map<number, number>()
		}
	});


	const addIsoline = (thresholdIndex: number, isoline: Isoline): number => {
		let isolineKey = fieldLines[thresholdIndex].isolines.length;
		fieldLines[thresholdIndex].isolineKeyMap.set(isolineKey, isolineKey);
		fieldLines[thresholdIndex].isolines.push(isoline);
		return isolineKey;
	}
	const getIsoline = (thresholdIndex: number, isolineKey: number): Isoline => {
		return isolineKey != undefined ? fieldLines[thresholdIndex].isolines[fieldLines[thresholdIndex].isolineKeyMap.get(isolineKey)] : undefined;
	}
	const connectIsolines = (thresholdIndex: number, headIsolineKey: number, tailIsolineKey: number): Isoline => {
		let headIsolineIndex = fieldLines[thresholdIndex].isolineKeyMap.get(headIsolineKey);
		let tailIsolineIndex = fieldLines[thresholdIndex].isolineKeyMap.get(tailIsolineKey);
		let headIsoline = fieldLines[thresholdIndex].isolines[headIsolineIndex];
		let tailIsoline = fieldLines[thresholdIndex].isolines[tailIsolineIndex];
		//head's last segment helper property equals tail's first segment helper property 
		let sameIsolineType = headIsoline[headIsoline.length - 1].helper === tailIsoline[0].helper;
		if (headIsolineIndex != tailIsolineIndex) {
			if (sameIsolineType) {
				//merge these segments
				let headLastSegment = headIsoline[headIsoline.length - 1];
				let tailFirstSegment = tailIsoline[0];
				headLastSegment.vertices.splice(headLastSegment.vertices.length, 0, ...tailFirstSegment.vertices);
			}
			//move the rest of the segments
			headIsoline.splice(headIsoline.length, 0, ...(tailIsoline.slice(sameIsolineType ? 1 : 0)));
			//tail Isoline is not needed any more
			tailIsoline = undefined;
			fieldLines[thresholdIndex].isolines[tailIsolineIndex] = undefined;
			//update the tailIsolinekey reference so that it points to the now extended head isoline
			fieldLines[thresholdIndex].isolineKeyMap.forEach((value, key, map) => {
				if (value === tailIsolineIndex) {
					map.set(key, headIsolineIndex);
				}
			});
			//			fieldLines[thresholdIndex].isolineKeyMap.set(tailIsolineKey, headIsolineIndex);
			//			fieldLines[thresholdIndex].isolineKeyMap.set(tailIsolineKey, fieldLines[thresholdIndex].isolineKeyMap.get(headIsolineKey));
		} else {
			//head and tail are actually same isoline, just close the isoline when the endings are not helper segments
			if (sameIsolineType && !tailIsoline[0].helper) {
				appendVertex(getFirstVertex(headIsoline), tailIsoline);
			}
		}
		return fieldLines[thresholdIndex].isolines[fieldLines[thresholdIndex].isolineKeyMap.get(headIsolineKey)];
	}

	let size = { x: iterationBounds.x.count, y: iterationBounds.y.count };
	//	let step = { x: iterationBounds.x.step, y: -iterationBounds.y.step };

	let firstColumn: boolean;
	let firstRow: boolean;
	let lastColumn: boolean;
	let lastRow: boolean;
	let intersection: Coord;
	let isolineIndex: number;
	let topIsolineIndex: number;
	let leftIsolineIndex: number;
	//the current isoline (isolines run counter clock wise)
	let isoline: Isoline;
	//the isoline comming from the top side into the current square (isolines run counter clock wise)
	let topIsoline: Isoline;
	//the isoline comming from the left side into the current square (isolines run counter clock wise)
	let leftIsoline: Isoline;
	let type: number;

	for (let scalar of iterator) {
		firstColumn = scalar.index.x === 0;
		firstRow = scalar.index.y === 0;
		lastColumn = scalar.index.x === size.x - 1;
		lastRow = scalar.index.y === size.y - 1;

		if (firstColumn) {//start of new line
		   //keep the previous scan line since it forms the top corners of the squares we now process
			prevScanLine = scanLine;
			scanLine = [];
		}

		//the current scalar is the bottom right corner of the current square
		bottomRightScanPoint = {
			scalar: scalar,
			type: thresholds.map(threshold => scalar != undefined && scalar.value != undefined && scalar.value >= threshold ? 1 : 0),
			downPropagatedIsolines: new Array(thresholds.length).fill(undefined),
			rightSidePropagatedIsolines: lastColumn ? new Array(thresholds.length).fill(undefined) : undefined,
			bottomSidePropagatedIsolines: lastRow ? new Array(thresholds.length).fill(undefined) : undefined,
			squareType: new Array(thresholds.length).fill(undefined)
		};

		//add the new (bottom right) corner to the current scanline
		scanLine.push(bottomRightScanPoint);

		// Since the algorithm travels from left to right and from top to bottom, the other corners of the
		// current square have already been constructed in previous steps and can be looked up.
		bottomLeftScanPoint = firstColumn ? undefined : scanLine[scalar.index.x - 1];
		topRightScanPoint = firstRow ? undefined : prevScanLine[scalar.index.x];
		topLeftScanPoint = firstRow || firstColumn ? undefined : prevScanLine[scalar.index.x - 1];

		if (firstRow) {//first row is a special case
			if (firstColumn) {//first column in first row is a very very special case
				thresholds.forEach((_t, i) => {
					//for debugging
				    bottomRightScanPoint.squareType[i] = bottomRightScanPoint.type[i]; 
					if (bottomRightScanPoint.type[i]) {

						//create the isoline
						isoline = [{ vertices: [toVertex(bottomRightScanPoint), toVertex(bottomRightScanPoint), toVertex(bottomRightScanPoint)], helper: true }];
						isolineIndex = addIsoline(i, isoline);

						//propagete down and right
						bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex;//propagation downwards
						rightPropagatedIsolines[i] = isolineIndex; //propagation to the right

					} else {
						//no propagation
						bottomRightScanPoint.downPropagatedIsolines[i] = undefined; //no propagation downwards
						rightPropagatedIsolines[i] = undefined; //no propagation to the right
					}
				});

			} else {
				//other columns on first row
				thresholds.forEach((threshold, i) => {
					type = bottomRightScanPoint.type[i] * 2 + bottomLeftScanPoint.type[i];
					//for debugging
				    bottomRightScanPoint.squareType[i] =type; 
					switch (type) {
						case 0: //0-0 no isoline
							//no propagation
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined; //no propagation downwards
							rightPropagatedIsolines[i] = undefined; //no propagation to the right
							break;
						case 1: //1-0 an isoline segment transitions into a horizontal helper segment
							//get the intersection vertex for given threshold
							intersection = bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint);

							//get the  isoline  
							isolineIndex = rightPropagatedIsolines[i];
							isoline = getIsoline(i, isolineIndex);

							replaceFirstVertex(intersection, isoline);
							prependSegment({ vertices: [intersection], helper: false }, isoline);

							//propagete down
							bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex; //propagation downwards
							rightPropagatedIsolines[i] = undefined; //no propagation to the right
							break;
						case 2://0-1 a horizontal helper segment transitions into an isoline segment
							//get the intersection vertex for given threshold
							intersection = bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint);

							//create the isoline
							isoline = [{ vertices: [toVertex(bottomRightScanPoint), intersection], helper: true }, { vertices: [intersection], helper: false }];
							isolineIndex = addIsoline(i, isoline);

							//propagete down and right
							bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex;//propagation downwards
							rightPropagatedIsolines[i] = isolineIndex; //propagation to the right

							if (lastColumn) {
								//when we reached the top right corner -> prepare for scanning downwards
								prependVertex(toVertex(bottomRightScanPoint), isoline);
								bottomRightScanPoint.rightSidePropagatedIsolines[i] = isolineIndex;
								rightPropagatedIsolines[i] = undefined; //no propagation to the right
							}
							break;
						case 3://1-1 a horizontal helper extends
							//get the  isoline  
							isolineIndex = rightPropagatedIsolines[i];
							isoline = getIsoline(i, isolineIndex);

							replaceFirstVertex(toVertex(bottomRightScanPoint), isoline);

							//propagete right
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined; //no propagation downwards
							rightPropagatedIsolines[i] = isolineIndex; //propagation to the right (unchanged)

							if (lastColumn) {
								//when we reached the top right corner -> prepare for scanning downwards
								prependVertex(toVertex(bottomRightScanPoint), isoline);
								bottomRightScanPoint.rightSidePropagatedIsolines[i] = isolineIndex;
								rightPropagatedIsolines[i] = undefined; //no propagation to the right
							}
							break;
					}
				});

			}
		} else {//other rows
			if (firstColumn) {//first column is a special case
				thresholds.forEach((threshold, i) => {
					type = topRightScanPoint.type[i] * 4 + bottomRightScanPoint.type[i] * 2;
					//for debugging
				    bottomRightScanPoint.squareType[i] =type; 
					switch (type) {
						case 0: //0-0 no isoline
							//no propagation
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined; //no propagation downwards
							rightPropagatedIsolines[i] = undefined; //no propagation to the right
							break;
						case 2: //0-1 an isoline segment transitions into a vertical helper segment
							//get the intersection vertex for given threshold
							intersection = rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint);

							//create the isoline
							isoline = [{ vertices: [intersection], helper: false }, { vertices: [toVertex(bottomRightScanPoint), intersection], helper: true }];
							isolineIndex = addIsoline(i, isoline);

							//propagete down and right
							bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex;//propagation downwards
							rightPropagatedIsolines[i] = isolineIndex; //propagation to the right
							
							if (lastRow) {
								//when we reached the bottom left corner -> prepare for scanning rightwards
								appendVertex(toVertex(bottomRightScanPoint), isoline);
								bottomRightScanPoint.bottomSidePropagatedIsolines[i] = isolineIndex;
								bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
								rightPropagatedIsolines[i] = isolineIndex;
							}
							
							break;
						case 4://1-0 a vertical helper segment transitions into an isoline segment
							//get the intersection vertex for given threshold
							intersection = rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint);

							//get the  isoline  
							isolineIndex = topRightScanPoint.downPropagatedIsolines[i]
							isoline = getIsoline(i, isolineIndex);

							replaceLastVertex(intersection, isoline);
							appendSegment({ vertices: [intersection], helper: false }, isoline);

							//propagete right
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined; //no propagation downwards
							rightPropagatedIsolines[i] = isolineIndex; // propagation to the right
							break;
						case 6://1-1 a vertical helper extends
							//get the  isoline  
							isolineIndex = topRightScanPoint.downPropagatedIsolines[i];
							isoline = getIsoline(i, isolineIndex);

							replaceLastVertex(toVertex(bottomRightScanPoint), isoline);

							//propagete down
							bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex; //propagation downwards (unchanged)
							rightPropagatedIsolines[i] = undefined; //no propagation to the right
							
							if (lastRow) {
								//when we reached the bottom left corner -> prepare for scanning rightwards
								appendVertex(toVertex(bottomRightScanPoint), isoline);
								bottomRightScanPoint.bottomSidePropagatedIsolines[i] = isolineIndex;
								bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
								rightPropagatedIsolines[i] = isolineIndex;
							}
 
							break;
					}
				});

			} else {//the standard case
				thresholds.forEach((threshold, i) => {
					//for debugging
				    bottomRightScanPoint.squareType[i] =type; 
					type = topLeftScanPoint.type[i] * 8 + topRightScanPoint.type[i] * 4 + bottomRightScanPoint.type[i] * 2 + bottomLeftScanPoint.type[i];
					topIsolineIndex = topRightScanPoint.downPropagatedIsolines[i];
					leftIsolineIndex = rightPropagatedIsolines[i];
					topIsoline = getIsoline(i, topIsolineIndex);
					leftIsoline = getIsoline(i, leftIsolineIndex);

					switch (type) {
						case 0:
							if (loggingEnabled && !(!topIsoline && !leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 0-0
							// | |
							// 0-0
							//no operation
							//no propagation
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
							rightPropagatedIsolines[i] = undefined;
							break;
						case 1:
							if (loggingEnabled && !(!topIsoline && leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 0-0
							// | |
							// 1-0
							//prepend bottom intersection to left isoline and propagate down
							prependVertex(bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint), leftIsoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = leftIsolineIndex;
							rightPropagatedIsolines[i] = undefined;
							break;
						case 2:
							if (loggingEnabled && !(!topIsoline && !leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 0-0
							// | |
							// 0-1
							//start new isoline from right intersection to bottom intersection and prpagate down & right
							isoline = [{
								vertices: [
									rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint),
									bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint)
								], helper: false
							}];
							isolineIndex = addIsoline(i, isoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex;
							rightPropagatedIsolines[i] = isolineIndex;
							break;
						case 3:
							if (loggingEnabled && !(!topIsoline && leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 0-0
							// | |
							// 1-1
							//prepend right intersection to left isoline and propagate right
							prependVertex(rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint), leftIsoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
							rightPropagatedIsolines[i] = leftIsolineIndex;
							break;
						case 4:
							if (loggingEnabled && !(topIsoline && !leftIsoline)) {
								log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
								console.debug(fieldLines);
							}
							// 0-1
							// | |
							// 0-0
							//append right intersection to top isoline and propagate right
							appendVertex(rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint), topIsoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
							rightPropagatedIsolines[i] = topIsolineIndex;
							break;
						case 5:
							if (loggingEnabled && !(topIsoline && leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							if ((topLeftScanPoint.scalar.value + topRightScanPoint.scalar.value + bottomRightScanPoint.scalar.value + bottomLeftScanPoint.scalar.value) / 4.0 < threshold) {
								// 0-1
								// |0|
								// 1-0
								//append right intersection to top isoline and propagate right
								//prepend bottom intersection to left isoline and propagate down
								appendVertex(rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint), topIsoline);
								rightPropagatedIsolines[i] = topIsolineIndex;
								prependVertex(bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint), leftIsoline);
								bottomRightScanPoint.downPropagatedIsolines[i] = leftIsolineIndex;
							} else {
								// 0-1
								// |1|
								// 1-0
								//connect top-end with left-start isoline
								//start new isoline from bottom intersection to right intersection and prpagate down & right
								connectIsolines(i, topIsolineIndex, leftIsolineIndex);
								isoline = [{
									vertices: [
										bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint),
										rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint)
									], helper: false
								}];
								isolineIndex = addIsoline(i, isoline);
								bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex;
								rightPropagatedIsolines[i] = isolineIndex;
							}
							break;
						case 6:
							if (loggingEnabled && !(topIsoline && !leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 0-1
							// | |
							// 0-1
							//append bottom intersection to top isoline and propagate down
							appendVertex(bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint), topIsoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = topIsolineIndex;
							rightPropagatedIsolines[i] = undefined;
							break;
						case 7:
							if (loggingEnabled && !(topIsoline && leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 0-1
							// | |
							// 1-1
							//connect top-end with left-start isoline no propagation
							connectIsolines(i, topIsolineIndex, leftIsolineIndex);
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
							rightPropagatedIsolines[i] = undefined;
							break;
						case 8:
							if (loggingEnabled && !(topIsoline && leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 1-0
							// | |
							// 0-0
							//connect left-end with top-start isoline no propagation
							connectIsolines(i, leftIsolineIndex, topIsolineIndex);
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
							rightPropagatedIsolines[i] = undefined;
							break;
						case 9:
							if (loggingEnabled && !(topIsoline && !leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 1-0
							// | |
							// 1-0
							//prepend bottom intersection to top isoline and propagate down
							prependVertex(bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint), topIsoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = topIsolineIndex;
							rightPropagatedIsolines[i] = undefined;
							break;
						case 10:
							if (loggingEnabled && !(topIsoline && leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							if ((topLeftScanPoint.scalar.value + topRightScanPoint.scalar.value + bottomRightScanPoint.scalar.value + bottomLeftScanPoint.scalar.value) / 4.0 < threshold) {
								// 1-0
								// |0|
								// 0-1
								//connect left-end with top-start isoline
								//start new isoline from right intersection to bottom intersection and prpagate down & right
								connectIsolines(i, leftIsolineIndex, topIsolineIndex);
								isoline = [{
									vertices: [
										rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint),
										bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint)
									], helper: false
								}];
								isolineIndex = addIsoline(i, isoline);
								bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex;
								rightPropagatedIsolines[i] = isolineIndex;
							} else {
								// 1-0
								// |1|
								// 0-1
								//prepend right intersection to top isoline and propagate right
								//append bottom intersection to left isoline and propagate down
								prependVertex(rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint), topIsoline);
								rightPropagatedIsolines[i] = topIsolineIndex;
								appendVertex(bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint), leftIsoline);
								bottomRightScanPoint.downPropagatedIsolines[i] = leftIsolineIndex;
							}
							break;
						case 11:
							if (loggingEnabled && !(topIsoline && !leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 1-0
							// | |
							// 1-1
							//prepend right intersection to top isoline and propagate right
							prependVertex(rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint), topIsoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
							rightPropagatedIsolines[i] = topIsolineIndex;
							break;
						case 12:
							if (loggingEnabled && !(!topIsoline && leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 1-1
							// | |
							// 0-0
							//append right intersection to left isoline and propagate right
							appendVertex(rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint), leftIsoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
							rightPropagatedIsolines[i] = leftIsolineIndex;
							break;
						case 13:
							if (loggingEnabled && !(!topIsoline && !leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 1-1
							// | |
							// 1-0
							//start new isoline from bottom intersection to right intersection and prpagate down & right
							isoline = [{
								vertices: [
									bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint),
									rightIntersection(threshold, bottomRightScanPoint, topRightScanPoint)
								], helper: false
							}];
							isolineIndex = addIsoline(i, isoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = isolineIndex;
							rightPropagatedIsolines[i] = isolineIndex;
							break;
						case 14:
							if (loggingEnabled && !(!topIsoline && leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 1-1
							// | |
							// 0-1
							//append bottom intersection to left isoline and propagate down
							appendVertex(bottomIntersection(threshold, bottomLeftScanPoint, bottomRightScanPoint), leftIsoline);
							bottomRightScanPoint.downPropagatedIsolines[i] = leftIsolineIndex;
							rightPropagatedIsolines[i] = undefined;
							break;
						case 15:
							if (loggingEnabled && !(!topIsoline && !leftIsoline)) log(bottomRightScanPoint, type, topIsolineIndex, topIsoline, leftIsolineIndex, leftIsoline);
							// 1-1
							// | |
							// 1-1
							//no operation
							//no propagation
							bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
							rightPropagatedIsolines[i] = undefined;
							break;
					}
					if (lastColumn) {
						switch (type) {
							case 0:
							case 1:
							case 8:
							case 9:
								//0--0
								bottomRightScanPoint.rightSidePropagatedIsolines[i] = undefined;
								rightPropagatedIsolines[i] = undefined;
								break;
							case 2:
							case 3:
							case 10:
							case 11:
								//0--1
								leftIsolineIndex = rightPropagatedIsolines[i];
								leftIsoline = getIsoline(i, leftIsolineIndex);
								prependSegment({ vertices: [toVertex(bottomRightScanPoint), getFirstVertex(leftIsoline)], helper: true }, leftIsoline);
								bottomRightScanPoint.rightSidePropagatedIsolines[i] = leftIsolineIndex;
								rightPropagatedIsolines[i] = undefined;
								break;
							case 4:
							case 5:
							case 12:
							case 13:
								//1--0
								topIsolineIndex = topRightScanPoint.rightSidePropagatedIsolines[i];
								leftIsolineIndex = rightPropagatedIsolines[i];
								topIsoline = getIsoline(i, topIsolineIndex);
								leftIsoline = getIsoline(i, leftIsolineIndex);
								replaceFirstVertex(getLastVertex(leftIsoline), topIsoline);
								connectIsolines(i, leftIsolineIndex, topIsolineIndex);
								bottomRightScanPoint.rightSidePropagatedIsolines[i] = undefined;
								rightPropagatedIsolines[i] = undefined;
								break;
							case 6:
							case 7:
							case 14:
							case 15:
								//1--1
								topIsolineIndex = topRightScanPoint.rightSidePropagatedIsolines[i];
								topIsoline = getIsoline(i, topIsolineIndex);
								replaceFirstVertex(toVertex(bottomRightScanPoint), topIsoline);
								bottomRightScanPoint.rightSidePropagatedIsolines[i] = topIsolineIndex;
								rightPropagatedIsolines[i] = undefined;
								break;
						}
					}
					if (lastRow) {

						switch (type) {
							case 0:
							case 4:
							case 8:
							case 12:
								//0--0
								bottomRightScanPoint.bottomSidePropagatedIsolines[i] = undefined;
								bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
								break;
							case 2:
							case 6:
							case 10:
							case 14:
								//0--1
								topIsolineIndex = bottomRightScanPoint.downPropagatedIsolines[i];
								topIsoline = getIsoline(i, topIsolineIndex);
								appendSegment({ vertices: [getLastVertex(topIsoline), toVertex(bottomRightScanPoint)], helper: true }, topIsoline);
								bottomRightScanPoint.bottomSidePropagatedIsolines[i] = topIsolineIndex;
								bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
								if (lastColumn) {
									connectIsolines(i, bottomRightScanPoint.rightSidePropagatedIsolines[i], bottomRightScanPoint.bottomSidePropagatedIsolines[i]);
								}
								break;
							case 1:
							case 5:
							case 9:
							case 13:
								//1--0
								topIsolineIndex = bottomRightScanPoint.downPropagatedIsolines[i];
								leftIsolineIndex = bottomLeftScanPoint.bottomSidePropagatedIsolines[i];
								topIsoline = getIsoline(i, topIsolineIndex);
								leftIsoline = getIsoline(i, leftIsolineIndex);
								replaceLastVertex(getFirstVertex(topIsoline), leftIsoline);
								connectIsolines(i, topIsolineIndex, leftIsolineIndex);
								bottomRightScanPoint.bottomSidePropagatedIsolines[i] = undefined;
								bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
								break;
							case 3:
							case 7:
							case 11:
							case 15:
								//1--1
								leftIsolineIndex = bottomLeftScanPoint.bottomSidePropagatedIsolines[i];
								leftIsoline = getIsoline(i, leftIsolineIndex);
								replaceLastVertex(toVertex(bottomRightScanPoint), leftIsoline);
								bottomRightScanPoint.bottomSidePropagatedIsolines[i] = leftIsolineIndex;
								bottomRightScanPoint.downPropagatedIsolines[i] = undefined;
								if (lastColumn) {
									connectIsolines(i, bottomRightScanPoint.rightSidePropagatedIsolines[i], bottomRightScanPoint.bottomSidePropagatedIsolines[i]);
								}
								break;
						}
					}

					//					if (type != 0 && type != 15) {
					//						console.debug('x=%s y=%s type=%s topKey=%s leftKey=%s', bottomRightScanPoint.scalar.index.x, bottomRightScanPoint.scalar.index.y, type, topIsolineIndex, leftIsolineIndex);
					//						console.debug(fieldlineToString(fieldLines[i], [235000, 4146000], 1, 0))
					//					}

				});
			}
		}
	}

	let result: { value: number, lines: LineString[][] }[] = fieldLines.map(fieldLine => {
		return {
			value: fieldLine.value,
			lines: fieldLine.isolines
				.filter(isoline => isoline && isoline.length > 0 && isoline[0].vertices.length > 0)
				.map(isoline => isoline.map(segment => { return { boundary: segment.helper, points: segment.vertices } })
				)
		}
	})

	return result;
}


/**
 * This method finds the isolines for given thresholds.
 * @param scalarField the scalar field where to find the isolines for
 * @param thresholds the scalar values for which to find the isolines in given scalar field
 */
export const getIsolinesFromScalarField = (scalarField: ScalarField, thresholds: number[]): { value: number, lines: LineString[][] }[] => {
	return getIsolines(scalarField.iterator(), scalarField.iterationBounds(), thresholds);
}
/**
 * This method finds the isolines for given thresholds.
 * @param iterator an Iterable  over ScalarFieldPoints
 * @param iterationBounds the bounds of the iterator
 * @param thresholds the scalar values for which to find the isolines in given scalar field
 */
export const getIsolines = (iterator: Iterable<ScalarFieldPoint>, iterationBounds: IterationBounds, thresholds: number[]): { value: number, lines: LineString[][] }[] => {
	return marchSquares(iterator, iterationBounds, thresholds);
}
